// src/pages/family/ChildrenPage.tsx
// Children list page with grid/list view, filtering, and CRUD modals

import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, ChevronRight, Home, Users, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { useChildren } from '@/features/children/hooks/useChildren';
import { ChildCard } from '@/features/children/components/ChildCard';
import { ChildCardSkeletonGrid, ChildCardSkeletonList } from '@/features/children/components/ChildCardSkeleton';
import { ChildFilters } from '../components/ChildFilters';
import { EmptyChildrenState } from '../components/EmptyChildrenState';
import { AddChildModal } from '@/features/children/components/AddChildModal';
import { EditChildModal } from '@/features/children/components/EditChildModal';
import { DeleteChildModal } from '@/features/children/components/DeleteChildModal';
import { useFamilyContext } from '@/features/family/hooks/useFamilyContext';
import { NoFamilyState } from '@/features/family/components/NoFamilyState';
import type { Child, ChildFilters as ChildFiltersType } from '@/features/children/types/childTypes';

export function ChildrenPage() {
  const { familyId, isLoading: isFamilyLoading, hasFamily } = useFamilyContext();

  // UI State
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters, setFilters] = useState<ChildFiltersType>({});

  // Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [deletingChild, setDeletingChild] = useState<Child | null>(null);

  // Build query params from filters
  const queryParams = useMemo(() => ({
    search: filters.search,
    diagnosis_id: filters.diagnosis_id,
    age_min: filters.age_min,
    age_max: filters.age_max,
    sort_by: filters.sort_by || 'name',
    sort_order: filters.sort_order || 'asc',
    page_size: 50, // Load all for now, could implement pagination
  }), [filters]);

  // Fetch children (only when we have a familyId)
  const {
    data: childrenData,
    isLoading,
    isError,
    error,
    refetch,
  } = useChildren(familyId ?? '', queryParams);

  const children = childrenData?.items ?? [];
  const totalCount = childrenData?.total ?? 0;

  // Calculate filtered count (if filters are applied)
  const filteredCount = useMemo(() => {
    if (!filters.search && !filters.diagnosis_id && !filters.age_min && !filters.age_max) {
      return undefined; // No filters active
    }
    return children.length;
  }, [children.length, filters]);

  // Handlers
  const handleAddChild = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const handleEditChild = useCallback((child: Child) => {
    setEditingChild(child);
  }, []);

  const handleDeleteChild = useCallback((child: Child) => {
    setDeletingChild(child);
  }, []);

  const handleAddSuccess = useCallback(() => {
    console.log('Child added successfully');
  }, []);

  const handleEditSuccess = useCallback(() => {
    console.log('Child updated successfully');
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    console.log('Child removed successfully');
  }, []);

  // Show loading state while family context is loading
  if (isFamilyLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-sage animate-spin mx-auto mb-4" />
          <p className="text-warm-gray">Loading family data...</p>
        </div>
      </div>
    );
  }

  // Show no family state if user doesn't have a family
  if (!hasFamily || !familyId) {
    return <NoFamilyState />;
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Page Header */}
      <div className="bg-white border-b border-warm-gray/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-warm-gray mb-4">
            <Link to="/family/dashboard" className="hover:text-sage transition-colors">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-navy font-medium">Children</span>
          </nav>

          {/* Title Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sage/10 to-sage/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-sage" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-semibold text-navy">
                  Children
                </h1>
                <p className="text-warm-gray">
                  Manage your children's profiles and information
                </p>
              </div>
            </div>

            <button
              onClick={handleAddChild}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-sage to-sage-dark text-white font-medium shadow-lg shadow-sage/20 hover:shadow-xl hover:shadow-sage/30 hover:-translate-y-0.5 transition-all"
            >
              <UserPlus className="w-5 h-5" />
              Add Child
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-8">
          <ChildFilters
            filters={filters}
            onFiltersChange={setFilters}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            totalCount={totalCount}
            filteredCount={filteredCount}
          />
        </div>

        {/* Content */}
        {isLoading ? (
          viewMode === 'grid' ? (
            <ChildCardSkeletonGrid count={6} />
          ) : (
            <ChildCardSkeletonList count={4} />
          )
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="font-display text-xl font-semibold text-navy mb-2">
              Failed to load children
            </h3>
            <p className="text-warm-gray text-center max-w-md mb-6">
              {error instanceof Error
                ? error.message
                : 'An unexpected error occurred while loading children.'}
            </p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sage text-white hover:bg-sage-dark transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        ) : children.length === 0 ? (
          <EmptyChildrenState
            onAddChild={handleAddChild}
            isFiltered={Boolean(filters.search || filters.diagnosis_id || filters.age_min || filters.age_max)}
          />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((child) => (
              <ChildCard
                key={child.id}
                child={child}
                familyId={familyId}
                variant="grid"
                onEdit={handleEditChild}
                onDelete={handleDeleteChild}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {children.map((child) => (
              <ChildCard
                key={child.id}
                child={child}
                familyId={familyId}
                variant="list"
                onEdit={handleEditChild}
                onDelete={handleDeleteChild}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddChildModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        familyId={familyId}
        onSuccess={handleAddSuccess}
      />

      {editingChild && (
        <EditChildModal
          isOpen={true}
          onClose={() => setEditingChild(null)}
          childId={editingChild.id}
          onSuccess={handleEditSuccess}
        />
      )}

      <DeleteChildModal
        isOpen={deletingChild !== null}
        onClose={() => setDeletingChild(null)}
        child={deletingChild}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}

export default ChildrenPage;
